import { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import { AsYouType, CountryCode, PhoneNumber } from 'libphonenumber-js';

import { TextField, TextFieldProps, Typography } from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

export interface PhoneNumberAsYouTypeProps {
  possible: boolean;
  valid: boolean;
  number: PhoneNumber | undefined;
  nationalNumber: string;
  country: CountryCode | undefined;
  template: string;
}

export type InputChangeEvent = ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;
export type PhoneInputProps = Omit<TextFieldProps, 'onChange'> & {
  onChange: (evt: InputChangeEvent, currentPhoneValueProps: Partial<PhoneNumberAsYouTypeProps>) => void;
}

export function PhoneInput(props: PhoneInputProps) {
  const { defaultValue, onChange, InputProps, ...rest } = props;

  const [numberState, setNumberState] = useState<PhoneNumberAsYouTypeProps | undefined>();
  
  const hasCountryCode = useMemo(() => {
    return numberState?.country;
  }, [numberState]);

  const isValid = useMemo(() => {
    return numberState?.valid;
  }, [numberState]);

  const onValueChange = (evt) => {
    const { value } = evt;
    computeAsYouType(value);
    onChange && onChange(evt, {...numberState});
  };

  const computeAsYouType = (v: string) => {
    const asYouType = new AsYouType();
    asYouType.input(v ?? '');
    setNumberState({
      country: asYouType.getCountry(),
      possible: asYouType.isPossible(),
      valid: asYouType.isValid(),
      number: asYouType.getNumber(),
      nationalNumber: asYouType.getNationalNumber(),
      template: asYouType.getTemplate()
    });
  };

  useEffect(() => {
    if (defaultValue && (typeof defaultValue === 'string')) {
      computeAsYouType(defaultValue)
    }
  }, [defaultValue]);

  return (
    <>
      <TextField
        {...rest}
        value={defaultValue}
        defaultValue={defaultValue}
        onChange={onValueChange}
        InputProps={{
          ...InputProps,
          startAdornment: hasCountryCode && (
            <Typography
              variant='subtitle2'
              color='secondary'
            >
              ({numberState?.country})
            </Typography>
          ),
          endAdornment: InputProps?.endAdornment ? (
            <Fragment>
              {isValid ? <CheckIcon /> : <ErrorOutlineIcon />}
              {InputProps.endAdornment}
            </Fragment> 
          ) : (
            isValid ? <CheckIcon /> : <ErrorOutlineIcon />
          )
        }}
      />
    </>
  );
}